import type { ApiResult } from './types';
import { caseApiRequest } from './apiClient';

const str = (v: unknown) => (typeof v === 'string' ? v.trim() : '');
const bool = (v: unknown) => (typeof v === 'boolean' ? v : undefined);

export const NATIONALITY_NAME_MAX_LENGTH = 100;
export const NATIONALITY_DESCRIPTION_MAX_LENGTH = 255;

export type NationalityListRow = {
  id: string;
  name: string;
  code: string;
  description: string;
  active: boolean;
  displayOrder: number;
  createdAt: string;
  createdBy: string;
  updatedAt: string;
  updatedBy: string;
};

export type NationalityDetail = NationalityListRow;

export type CreateNationalityInput = {
  name: string;
  description: string;
  active: boolean;
};

export type UpdateNationalityInput = CreateNationalityInput;

type ApiNationalityRow = Record<string, unknown>;

function mapNationalityRow(row: ApiNationalityRow): NationalityListRow | null {
  const id = str(row.id);
  const name = str(row.name);
  if (!id || !name) return null;

  return {
    id,
    name,
    code: str(row.code),
    description: str(row.description),
    active: bool(row.active) ?? true,
    displayOrder:
      typeof row.display_order === 'number'
        ? row.display_order
        : typeof row.displayOrder === 'number'
          ? row.displayOrder
          : 0,
    createdAt: str(row.created_at ?? row.createdAt),
    createdBy: str(row.created_by ?? row.createdBy),
    updatedAt: str(row.updated_at ?? row.updatedAt),
    updatedBy: str(row.updated_by ?? row.updatedBy),
  };
}

function unwrapRows(data: unknown): ApiNationalityRow[] {
  if (Array.isArray(data)) return data as ApiNationalityRow[];
  if (!data || typeof data !== 'object') return [];
  const record = data as Record<string, unknown>;
  if (Array.isArray(record.content)) return record.content as ApiNationalityRow[];
  if (Array.isArray(record.data)) return record.data as ApiNationalityRow[];
  return [];
}

/** GET /v1/masters/nationalities */
export async function getNationalities(): Promise<ApiResult<NationalityListRow[]>> {
  const res = await caseApiRequest<unknown>('/v1/masters/nationalities', { method: 'GET' });
  if (!res.ok) return { success: false, message: res.message };
  const rows = unwrapRows(res.data)
    .map(mapNationalityRow)
    .filter((row): row is NationalityListRow => row != null);
  return { success: true, data: rows };
}

/** GET /v1/masters/nationalities/{id} */
export async function getNationalityById(id: string): Promise<ApiResult<NationalityDetail>> {
  const nationalityId = id.trim();
  if (!nationalityId) return { success: false, message: 'Nationality id is missing.' };
  const res = await caseApiRequest<unknown>(
    `/v1/masters/nationalities/${encodeURIComponent(nationalityId)}`,
    { method: 'GET' },
  );
  if (!res.ok) return { success: false, message: res.message };
  const detail = mapNationalityRow(
    res.data && typeof res.data === 'object' ? (res.data as ApiNationalityRow) : {},
  );
  if (!detail) return { success: false, message: 'Nationality details are unavailable.' };
  return { success: true, data: detail };
}

/** POST /v1/masters/nationalities */
export async function createNationality(input: CreateNationalityInput): Promise<ApiResult<void>> {
  const res = await caseApiRequest<unknown>('/v1/masters/nationalities', {
    method: 'POST',
    body: {
      name: input.name.trim(),
      description: input.description.trim(),
      active: input.active,
    },
  });
  if (!res.ok) return { success: false, message: res.message };
  return { success: true, data: undefined };
}

/** PUT /v1/masters/nationalities/{id} */
export async function updateNationality(
  id: string,
  input: UpdateNationalityInput,
): Promise<ApiResult<void>> {
  const nationalityId = id.trim();
  if (!nationalityId) return { success: false, message: 'Nationality id is missing.' };
  const res = await caseApiRequest<unknown>(
    `/v1/masters/nationalities/${encodeURIComponent(nationalityId)}`,
    {
      method: 'PUT',
      body: {
        name: input.name.trim(),
        description: input.description.trim(),
        active: input.active,
      },
    },
  );
  if (!res.ok) return { success: false, message: res.message };
  return { success: true, data: undefined };
}
